import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'semantic-ui-react';

import { useStreaks } from '../../shared/api/apiHooks';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import analytics from '../../shared/services/analytics/analytics';
import { AppDispatch } from '../../shared/services/store/types';
import { updateCustomPreferencesState } from '../../reducers/CustomPreferencesReducer/CustomPreferencesReducer';
import { getStreakProtectedShownDate } from '../../reducers/CustomPreferencesReducer/CustomPreferencesSelectors';
import { ErrorBoundary } from '../ErrorBoundary';
import { StreakWeekStrip } from './StreakWeekStrip';
import './StreakProtectedModal.scss';

interface StreakProtectedModalProps {
  parent: React.MutableRefObject<HTMLDivElement | null>;
}

// BRAINTECH-5837: shown the day after a freeze was applied, before the user starts that day's first
// simulation (per AC — not gated on `editor.isSimulated`, unlike the post-sim StreakModal). Dedupes
// per applied freeze via `streakProtectedShownDate` == `freeze.usedDate`.
export function StreakProtectedModal({ parent }: StreakProtectedModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { data } = useStreaks();
  const shownDate = useSelector(getStreakProtectedShownDate);
  const [isOpen, setIsOpen] = useState(false);

  const usedDate = data?.freeze?.usedDate ?? null;
  const shouldShow = !!data?.freeze?.appliedYesterday && usedDate != null && shownDate !== usedDate;

  useEffect(() => {
    // Guard on `parent.current` so the effect and the render (which bails out when the mount node is
    // absent) agree — otherwise the once-per-quarter dedupe preference and the analytics impression
    // would be consumed even when the modal can't actually show.
    // Note: this deliberately does NOT track `parent.current` mutations — a ref change doesn't re-run
    // the effect. In the CodeEditor tree the mount node is attached in the same commit as the ref, so
    // it is always present by the time streak data resolves; this guard only prevents the burn when
    // the node is genuinely absent.
    if (shouldShow && usedDate != null && parent.current) {
      setIsOpen(true);
      analytics.trackCustomEvent('streak_protected_modal_shown');
      dispatch(updateCustomPreferencesState({ streakProtectedShownDate: usedDate }, { optimistic: true }));
    }
  }, [shouldShow, usedDate, parent, dispatch]);

  if (parent == null || !parent.current || !data) {
    return <></>;
  }

  const handleClose = () => {
    setIsOpen(false);
    analytics.trackCustomEvent('streak_protected_modal_close');
  };

  const handleGotIt = () => {
    setIsOpen(false);
    analytics.trackCustomEvent('streak_protected_modal_click_got_it');
  };

  return (
    <ErrorBoundary showUI={true} isNotFullScreen={true} isModal={true}>
      <React.Fragment>
        <Modal
          className="streak-protected-modal"
          mountNode={parent.current}
          closeIcon={true}
          open={isOpen}
          onClose={handleClose}
          closeOnDimmerClick={false}
          size="tiny"
        >
          <Modal.Content>
            <StreakWeekStrip records={data.records} />
            <div className="streak-protected-modal__title">
              <FormattedMessageHelper
                id="streakProtected.modal.title"
                values={{
                  highlight: (chunks: React.ReactNode) => <span className="streak-protected-modal__title-highlight">{chunks}</span>,
                }}
              />
            </div>
            <div className="streak-protected-modal__body">
              <FormattedMessageHelper id="streakProtected.modal.body" />
            </div>
            <div className="streak-protected-modal__button-container">
              <button className="button button--primary" onClick={handleGotIt}>
                <FormattedMessageHelper id="streakProtected.modal.gotIt" />
              </button>
            </div>
          </Modal.Content>
        </Modal>
      </React.Fragment>
    </ErrorBoundary>
  );
}
